import { defineStore } from 'pinia';
import { ref, computed, type Ref } from 'vue';
import { CalendarMonth, CalendarMonthExtended } from '~/models/CalendarMonth';

/*
 * Хранилище календаря садовода (Pinia-store)
 *
 * Отвечает за:
 * - Хранение списка месяцев и активного месяца
 * - Предоставление списка месяцев и текущего активного месяца
 * - Обновление списка месяцев и смену активного месяца
 *
 * @function useCalendarMonthStore
 * @returns {{
 *   months: import('vue').ComputedRef<Array<CalendarMonth>>;
 *   activeMonth: import('vue').ComputedRef<CalendarMonthExtended>;
 *   setMonths: (months: Array<CalendarMonth>) => void;
 *   setActiveMonth: (month: CalendarMonthExtended) => void;
 * }}
 *
 * Методы и свойства:
 * - months - вычисляемый список месяцев
 * - activeMonth - вычисляемый активный месяц календаря
 * - setMonths - устанавливает/обновляет список месяцев
 * - setActiveMonth - устанавливает активный месяц
 */
export const useCalendarMonthStore = defineStore('useCalendarMonthStore', () => {
  /*
   * Список месяцев календаря
   * @type {Ref<Array<CalendarMonth>>}
   */
  const monthsList: Ref<Array<CalendarMonth>> = ref([]);

  /*
   * Текущее активное состояние месяца (расширенная модель)
   * @type {Ref<CalendarMonthExtended>}
   * @private
   */
  const _activeMonth: Ref<CalendarMonthExtended> = ref(new CalendarMonthExtended({}));

  /*
   * Вычисляемый список месяцев
   * @type {import('vue').ComputedRef<Array<CalendarMonth>>}
   */
  const months = computed((): CalendarMonth[] => monthsList.value);

  /*
   * Вычисляемый активный месяц
   * @type {import('vue').ComputedRef<CalendarMonthExtended>}
   */
  const activeMonth = computed((): CalendarMonthExtended => _activeMonth.value);

  /*
   * Устанавливает список месяцев
   * @param {Array<CalendarMonth>} months - массив моделей месяцев
   * @returns {void}
   */
  function setMonths(months: CalendarMonth[]): void {
    monthsList.value = months;
  }

  /*
   * Устанавливает активный месяц
   * @param {CalendarMonthExtended} month - модель активного месяца
   * @returns {void}
   */
  function setActiveMonth(month: CalendarMonthExtended): void {
    _activeMonth.value = month;
  }

  return {
    // state / getters
    months,
    activeMonth,

    // actions
    setMonths,
    setActiveMonth,
  };
});
